"use client";
import React, { useState, useEffect, useRef } from "react";
import { BASE_URL, UPLOAD_URL } from "../../config/config";
import { useParams, Link } from "react-router-dom";

function ProductSuggestions() {
  const { id } = useParams();
  const [products, setProducts] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  /* Scroll to top when product changes */
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  /* Fetch related products */
  useEffect(() => {
    async function fetchProducts() {
      try {
        const res = await fetch(`${BASE_URL}/related-products/${id}`);
        const json = await res.json();

        if (json.status === 200 && json.data) {
          const formattedProducts = json.data.map((item) => ({
            id: item.id,
            title: item.name,
            image: `${UPLOAD_URL}/products/${item.images?.split(",")[0]}`,
            price: Number(item.amount?.replace(/,/g, "")) || 0,
            offer_price: Number(item.offer_price) || 0,
          }));

          setProducts(formattedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setProducts([]);
      }
    }

    fetchProducts();
  }, [id]);

  return (
    <section className="w-full mt-8 mb-10">
      <h2 className="text-2xl font-bold text-neutral-700 mb-6">
        You Might Also Like
      </h2>

      <div
        ref={containerRef}
        className="
          grid 
          grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 
          gap-4 sm:gap-6
        "
      >
        {products.length > 0 ? (
          products.map((product, index) => {
            const price = product.price;
            const offerPrice = product.offer_price;

            const discount =
              price && offerPrice && price > offerPrice
                ? Math.round(((price - offerPrice) / price) * 100)
                : 0;

            return (
              <article
                key={index}
                className="
                  bg-white rounded-xl shadow-sm hover:shadow-md
                  transition-shadow duration-300 overflow-hidden
                  flex flex-col items-center
                "
              >
                <Link
                  to={`/products/${product.id}`}
                  className="block w-full h-full group"
                  onClick={(e) => {
                    if (isDragging) e.preventDefault();
                  }}
                >
                  {/* Image */}
                  <div className="relative w-full aspect-square bg-white flex justify-center items-center overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="
                        object-contain w-[90%] h-[90%]
                        group-hover:scale-105 transition-transform duration-300
                      "
                      draggable="false"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-3 md:p-4 text-center">
                    <h3 className="text-sm md:text-base font-medium text-neutral-800 line-clamp-2 mb-2">
                      {product.title}
                    </h3>

                    {/* Price Section */}
                    <div className="flex justify-center items-center gap-2 flex-wrap">
                      <span className="text-base font-semibold text-neutral-900">
                        ₹ {offerPrice || price}
                      </span>

                      {price > offerPrice && offerPrice > 0 && (
                        <>
                          <span className="text-sm text-neutral-400 line-through">
                            ₹ {price}
                          </span>

                          <span className="text-xs font-semibold text-green-600">
                            ({discount}% OFF)
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              </article>
            );
          })
        ) : (
          <p className="text-center w-full py-20 text-neutral-500">
            Loading products...
          </p>
        )}
      </div>
    </section>
  );
}

export default ProductSuggestions;
